import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

import { LINKS } from "../../config/links";
import { SEO_CONFIG } from "../../config/seo.config";

const SITE_URL = "https://zalewczorsztynski.com";
const SITE_NAME = "Czorsztynianka";

const normalizePath = (pathname) => {
    if (pathname === "/") {
        return "/";
    }

    return pathname.replace(/\/+$/, "");
};

const findRouteByPath = (pathname) => {
    for (const [page, localizedRoutes] of Object.entries(
        LINKS.routes,
    )) {
        if (localizedRoutes?.pl === pathname) {
            return {
                page,
                language: "pl",
            };
        }

        if (localizedRoutes?.en === pathname) {
            return {
                page,
                language: "en",
            };
        }
    }

    return null;
};

const RouteSeo = () => {
    const { pathname } = useLocation();

    const normalizedPath = normalizePath(pathname);
    const route = findRouteByPath(normalizedPath);

    if (!route) {
        return (
            <Helmet>
                <html lang="pl" />

                <title>
                    Nie znaleziono strony | Czorsztynianka
                </title>

                <meta
                    name="robots"
                    content="noindex, follow"
                />
            </Helmet>
        );
    }

    const { page, language } = route;

    const seo = SEO_CONFIG[language]?.[page];
    const localizedRoutes = LINKS.routes[page];

    if (!seo) {
        console.error(
            `Brak konfiguracji SEO dla: ${language}.${page}`,
        );

        return (
            <Helmet>
                <html lang={language} />
                <title>Czorsztynianka</title>
                <meta
                    name="robots"
                    content="noindex, follow"
                />
            </Helmet>
        );
    }

    const canonicalUrl = new URL(
        normalizedPath,
        SITE_URL,
    ).toString();

    const imageUrl = new URL(
        seo.image,
        SITE_URL,
    ).toString();

    const polishUrl = localizedRoutes?.pl
        ? new URL(localizedRoutes.pl, SITE_URL).toString()
        : null;

    const englishUrl = localizedRoutes?.en
        ? new URL(localizedRoutes.en, SITE_URL).toString()
        : null;

    return (
        <Helmet>
            <html lang={language} />

            <title>{seo.title}</title>

            <meta
                name="description"
                content={seo.description}
            />

            <meta
                name="robots"
                content={seo.robots}
            />

            <link
                rel="canonical"
                href={canonicalUrl}
            />

            {polishUrl && englishUrl && (
                <>
                    <link
                        rel="alternate"
                        hrefLang="pl"
                        href={polishUrl}
                    />

                    <link
                        rel="alternate"
                        hrefLang="en"
                        href={englishUrl}
                    />

                    <link
                        rel="alternate"
                        hrefLang="x-default"
                        href={polishUrl}
                    />
                </>
            )}

            <meta
                property="og:type"
                content="website"
            />

            <meta
                property="og:site_name"
                content={SITE_NAME}
            />

            <meta
                property="og:locale"
                content={
                    language === "en"
                        ? "en_GB"
                        : "pl_PL"
                }
            />

            <meta
                property="og:locale:alternate"
                content={
                    language === "en"
                        ? "pl_PL"
                        : "en_GB"
                }
            />

            <meta
                property="og:title"
                content={seo.title}
            />

            <meta
                property="og:description"
                content={seo.description}
            />

            <meta
                property="og:url"
                content={canonicalUrl}
            />

            <meta
                property="og:image"
                content={imageUrl}
            />

            <meta
                property="og:image:secure_url"
                content={imageUrl}
            />

            <meta
                property="og:image:type"
                content="image/jpeg"
            />

            <meta
                property="og:image:alt"
                content={seo.imageAlt}
            />

            <meta
                name="twitter:card"
                content="summary_large_image"
            />

            <meta
                name="twitter:title"
                content={seo.title}
            />

            <meta
                name="twitter:description"
                content={seo.description}
            />

            <meta
                name="twitter:image"
                content={imageUrl}
            />

            <meta
                name="twitter:image:alt"
                content={seo.imageAlt}
            />
        </Helmet>
    );
};

export default RouteSeo;